import { BitMask, makeMask, maskSet } from './textGen';

/**
 * A stroke alphabet for box drawing.
 *
 * Tracing the contour of a font gives *two* lines per stroke — the outside and
 * the inside — so letters come out wide and hollow. Box-drawn lettering wants
 * the opposite: one line per stroke, narrow and rectilinear, exactly the
 * skeleton a pen would draw. No font provides that at cell resolution, so the
 * alphabet is defined here directly: five columns by seven rows, every stroke
 * one cell wide, and no diagonals that box characters cannot join.
 *
 * Feeding this through the usual box connectivity turns each glyph into ║ ═ ╔
 * ╗ ╚ ╝ ╠ ╣ ╦ ╩ ╬ automatically.
 */

export const STROKE_W = 5;
export const STROKE_H = 7;

/** '#' is a stroke cell, '.' is empty. */
const GLYPHS: Record<string, string[]> = {
  A: ['.###.', '#...#', '#...#', '#####', '#...#', '#...#', '#...#'],
  B: ['####.', '#...#', '#...#', '####.', '#...#', '#...#', '####.'],
  C: ['.####', '#....', '#....', '#....', '#....', '#....', '.####'],
  D: ['####.', '#...#', '#...#', '#...#', '#...#', '#...#', '####.'],
  E: ['#####', '#....', '#....', '####.', '#....', '#....', '#####'],
  F: ['#####', '#....', '#....', '####.', '#....', '#....', '#....'],
  G: ['.####', '#....', '#....', '#.###', '#...#', '#...#', '.####'],
  H: ['#...#', '#...#', '#...#', '#####', '#...#', '#...#', '#...#'],
  I: ['#####', '..#..', '..#..', '..#..', '..#..', '..#..', '#####'],
  J: ['.####', '...#.', '...#.', '...#.', '...#.', '#..#.', '.##..'],
  K: ['#...#', '#..#.', '#.#..', '##...', '#.#..', '#..#.', '#...#'],
  L: ['#....', '#....', '#....', '#....', '#....', '#....', '#####'],
  M: ['#...#', '##.##', '#.#.#', '#...#', '#...#', '#...#', '#...#'],
  N: ['#...#', '##..#', '#.#.#', '#..##', '#...#', '#...#', '#...#'],
  O: ['.###.', '#...#', '#...#', '#...#', '#...#', '#...#', '.###.'],
  P: ['####.', '#...#', '#...#', '####.', '#....', '#....', '#....'],
  Q: ['.###.', '#...#', '#...#', '#...#', '#.#.#', '#..#.', '.##.#'],
  R: ['####.', '#...#', '#...#', '####.', '#.#..', '#..#.', '#...#'],
  S: ['.####', '#....', '#....', '.###.', '....#', '....#', '####.'],
  T: ['#####', '..#..', '..#..', '..#..', '..#..', '..#..', '..#..'],
  U: ['#...#', '#...#', '#...#', '#...#', '#...#', '#...#', '.###.'],
  V: ['#...#', '#...#', '#...#', '#...#', '#...#', '.#.#.', '..#..'],
  W: ['#...#', '#...#', '#...#', '#...#', '#.#.#', '##.##', '#...#'],
  X: ['#...#', '#...#', '.#.#.', '..#..', '.#.#.', '#...#', '#...#'],
  Y: ['#...#', '#...#', '.#.#.', '..#..', '..#..', '..#..', '..#..'],
  Z: ['#####', '....#', '...#.', '..#..', '.#...', '#....', '#####'],

  '0': ['.###.', '#...#', '#..##', '#.#.#', '##..#', '#...#', '.###.'],
  '1': ['..#..', '.##..', '..#..', '..#..', '..#..', '..#..', '.###.'],
  '2': ['.###.', '#...#', '....#', '...#.', '..#..', '.#...', '#####'],
  '3': ['####.', '....#', '....#', '.###.', '....#', '....#', '####.'],
  '4': ['#..#.', '#..#.', '#..#.', '#####', '...#.', '...#.', '...#.'],
  '5': ['#####', '#....', '#....', '####.', '....#', '....#', '####.'],
  '6': ['.###.', '#....', '#....', '####.', '#...#', '#...#', '.###.'],
  '7': ['#####', '....#', '...#.', '..#..', '..#..', '..#..', '..#..'],
  '8': ['.###.', '#...#', '#...#', '.###.', '#...#', '#...#', '.###.'],
  '9': ['.###.', '#...#', '#...#', '.####', '....#', '....#', '.###.'],

  ' ': ['.....', '.....', '.....', '.....', '.....', '.....', '.....'],
  '.': ['.....', '.....', '.....', '.....', '.....', '.....', '..#..'],
  ',': ['.....', '.....', '.....', '.....', '.....', '..#..', '.#...'],
  ':': ['.....', '.....', '..#..', '.....', '..#..', '.....', '.....'],
  ';': ['.....', '.....', '..#..', '.....', '..#..', '.#...', '.....'],
  '!': ['..#..', '..#..', '..#..', '..#..', '..#..', '.....', '..#..'],
  '?': ['.###.', '#...#', '....#', '...#.', '..#..', '.....', '..#..'],
  '-': ['.....', '.....', '.....', '#####', '.....', '.....', '.....'],
  '_': ['.....', '.....', '.....', '.....', '.....', '.....', '#####'],
  '=': ['.....', '.....', '#####', '.....', '#####', '.....', '.....'],
  '+': ['.....', '..#..', '..#..', '#####', '..#..', '..#..', '.....'],
  '*': ['.....', '#...#', '.#.#.', '#####', '.#.#.', '#...#', '.....'],
  '/': ['....#', '....#', '...#.', '..#..', '.#...', '#....', '#....'],
  '\\': ['#....', '#....', '.#...', '..#..', '...#.', '....#', '....#'],
  '|': ['..#..', '..#..', '..#..', '..#..', '..#..', '..#..', '..#..'],
  '(': ['...#.', '..#..', '..#..', '..#..', '..#..', '..#..', '...#.'],
  ')': ['.#...', '..#..', '..#..', '..#..', '..#..', '..#..', '.#...'],
  '[': ['..###', '..#..', '..#..', '..#..', '..#..', '..#..', '..###'],
  ']': ['###..', '..#..', '..#..', '..#..', '..#..', '..#..', '###..'],
  '<': ['...#.', '..#..', '.#...', '#....', '.#...', '..#..', '...#.'],
  '>': ['.#...', '..#..', '...#.', '....#', '...#.', '..#..', '.#...'],
  '#': ['.#.#.', '.#.#.', '#####', '.#.#.', '#####', '.#.#.', '.#.#.'],
  '@': ['.###.', '#...#', '#.###', '#.#.#', '#.###', '#....', '.###.'],
  '&': ['.##..', '#..#.', '#.#..', '.#...', '#.#.#', '#..#.', '.##.#'],
  '%': ['#...#', '....#', '...#.', '..#..', '.#...', '#....', '#...#'],
  "'": ['..#..', '..#..', '.....', '.....', '.....', '.....', '.....'],
  '"': ['.#.#.', '.#.#.', '.....', '.....', '.....', '.....', '.....'],
  '^': ['..#..', '.#.#.', '#...#', '.....', '.....', '.....', '.....'],
  '~': ['.....', '.....', '.##.#', '#..#.', '.....', '.....', '.....'],
  $: ['..#..', '.####', '#.#..', '.###.', '..#.#', '####.', '..#..'],
};

export function hasStrokeGlyph(char: string): boolean {
  return GLYPHS[char.toUpperCase()] !== undefined;
}

export interface StrokeTextOptions {
  /** Cells between letters. One keeps strokes from touching. */
  letterSpacing: number;
  /** Cells between lines. */
  lineSpacing: number;
}

export const DEFAULT_STROKE_OPTIONS: StrokeTextOptions = {
  letterSpacing: 1,
  lineSpacing: 1,
};

/**
 * Lays out stroke-font text into a bitmap, one dot per cell.
 *
 * The alphabet has no size control on purpose. Its strokes are one cell wide by
 * definition, and spreading the cells apart to "scale" it breaks the adjacency
 * the box characters join along: a bar and the stem it meets are diagonal
 * neighbours, which squares off cleanly at 1:1 and falls apart at any other
 * ratio. Narrow, fixed-size lettering is what this font is for.
 */
export function renderStrokeText(text: string, options: Partial<StrokeTextOptions> = {}): BitMask {
  const opts = { ...DEFAULT_STROKE_OPTIONS, ...options };
  const lines = text.split('\n');

  const stepX = STROKE_W + Math.max(0, opts.letterSpacing);
  const stepY = STROKE_H + Math.max(0, opts.lineSpacing);

  const longest = Math.max(1, ...lines.map((l) => [...l].length));
  // Spacing sits between glyphs, so the last one adds none: otherwise every
  // rendering carried a blank trailing column and row.
  const width = longest * STROKE_W + (longest - 1) * Math.max(0, opts.letterSpacing);
  const height = lines.length * STROKE_H + (lines.length - 1) * Math.max(0, opts.lineSpacing);
  const mask = makeMask(Math.max(1, width), Math.max(1, height));

  lines.forEach((line, row) => {
    [...line].forEach((raw, col) => {
      const glyph = GLYPHS[raw.toUpperCase()];
      if (!glyph) return;
      const originX = col * stepX;
      const originY = row * stepY;
      for (let y = 0; y < STROKE_H; y++) {
        for (let x = 0; x < STROKE_W; x++) {
          if (glyph[y][x] === '#') maskSet(mask, originX + x, originY + y, true);
        }
      }
    });
  });

  return mask;
}

/** Characters the alphabet covers, for the panel's hint. */
export function strokeCoverage(): string {
  return Object.keys(GLYPHS).filter((k) => k !== ' ').join('');
}
